/**
 * 画像結合ユーティリティ
 * 2つの画像を横並びでくっつける機能
 * Pythonスクリプト（backend/utils/image_combiner.py）を参考に実装
 */

/**
 * 2つの画像を横並びで結合する
 * 
 * @param {string} leftImageUrl - 左側画像のURL
 * @param {string} rightImageUrl - 右側画像のURL
 * @param {Object} options - オプション設定
 * @param {number} options.scaleRatio - 右側画像の拡大・縮小率 (1.0 = 等倍)
 * @param {boolean} options.matchHeight - 高さを左側画像に合わせるかどうか
 * @param {string} options.backgroundColor - 背景色 (デフォルト: '#000000')
 * @returns {Promise<string>} - 結合された画像のDataURL
 */
export const combineImages = async (leftImageUrl, rightImageUrl, options = {}) => {
  const {
    scaleRatio = 1.0,
    matchHeight = false,
    backgroundColor = '#000000'
  } = options;

  try {
    // 画像を読み込み
    const leftImage = await loadImage(leftImageUrl);
    const rightImage = await loadImage(rightImageUrl);

    // 右側画像のリサイズ処理
    let resizedRightImage = rightImage;
    if (matchHeight) {
      // 左側画像の高さに合わせて右側画像をリサイズ
      const targetHeight = leftImage.height;
      const scaleFactor = targetHeight / rightImage.height;
      const rightWidth = Math.round(rightImage.width * scaleFactor);
      resizedRightImage = await resizeImage(rightImage, rightWidth, targetHeight);
    } else if (scaleRatio !== 1.0) {
      // 指定された拡大率でリサイズ
      const rightWidth = Math.round(rightImage.width * scaleRatio);
      const rightHeight = Math.round(rightImage.height * scaleRatio);
      resizedRightImage = await resizeImage(rightImage, rightWidth, rightHeight);
    }

    // 結合後の画像サイズを計算
    const combinedWidth = leftImage.width + resizedRightImage.width;
    const combinedHeight = Math.max(leftImage.height, resizedRightImage.height);

    // Canvasを作成
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = combinedWidth;
    canvas.height = combinedHeight;

    // 背景色を設定
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, combinedWidth, combinedHeight);

    // 左側画像を配置（縦方向センター合わせ）
    const leftY = Math.round((combinedHeight - leftImage.height) / 2);
    ctx.drawImage(leftImage, 0, leftY);

    // 右側画像を配置（縦方向センター合わせ）
    const rightY = Math.round((combinedHeight - resizedRightImage.height) / 2);
    ctx.drawImage(resizedRightImage, leftImage.width, rightY);

    // DataURLとして返す
    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error('画像の結合に失敗しました:', error);
    throw error;
  }
};

/**
 * 画像をURLから読み込む
 * @param {string} url - 画像のURL
 * @returns {Promise<HTMLImageElement>}
 */
const loadImage = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    // DataURLの場合はcrossOriginを設定しない
    if (!url.startsWith('data:')) {
      img.crossOrigin = 'anonymous'; // CORS対応
    }
    
    img.onload = () => {
      resolve(img);
    };
    img.onerror = (error) => {
      // CORSエラーの場合はcrossOriginを外して再試行
      if (error.type === 'error' && !url.startsWith('data:')) {
        const retryImg = new Image();
        retryImg.onload = () => {
          resolve(retryImg);
        };
        retryImg.onerror = (retryError) => {
          reject(new Error(`画像の読み込みに失敗しました: ${url}`));
        };
        retryImg.src = url;
      } else {
        reject(new Error(`画像の読み込みに失敗しました: ${url}`));
      }
    };
    img.src = url;
  });
};

/**
 * 画像をリサイズする（非同期版）
 * @param {HTMLImageElement} image - 元画像
 * @param {number} width - 新しい幅
 * @param {number} height - 新しい高さ
 * @returns {Promise<HTMLImageElement>} - リサイズされた画像
 */
const resizeImage = (image, width, height) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = width;
    canvas.height = height;
    
    // 高品質なリサイズのための設定
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    ctx.drawImage(image, 0, 0, width, height);
    
    // リサイズされた画像を新しいImageオブジェクトとして返す
    const resizedImage = new Image();
    resizedImage.onload = () => {
      resolve(resizedImage);
    };
    resizedImage.onerror = (error) => {
      reject(new Error('リサイズ画像の読み込みに失敗しました'));
    };
    resizedImage.src = canvas.toDataURL();
  });
};

/**
 * DataURLをBlobに変換する
 * @param {string} dataURL - DataURL
 * @returns {Blob}
 */
export const dataURLToBlob = (dataURL) => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

/**
 * BlobをFileオブジェクトに変換する
 * @param {Blob} blob - Blobオブジェクト
 * @param {string} filename - ファイル名
 * @returns {File}
 */
export const blobToFile = (blob, filename) => {
  return new File([blob], filename, { type: blob.type });
}; 